import { portalText } from "../../i18n/portal.js";
/**
 * The owner's lifecycle actions on a VoiceAgent, and the build read (customer-portal review
 * 2026-09-25, #105, #106, #32, #33, #28).
 *
 *   pauseVoiceAgent / resumeVoiceAgent   `POST /v1/agents/{id}/pause` · `/resume`  (#106)
 *   archiveVoiceAgent                    `POST /v1/agents/{id}/archive`            (#105)
 *   renameVoiceAgent                     `PUT /v1/agents/{id}/name`                (owner, 2026-10-28)
 *   rebuildVoiceAgent                    `POST /v1/agents/{id}/rebuild`            (#32)
 *   getBuildJob                          `GET /v1/builds/{build_job_id}`           (#28)
 *
 * Each change answers the VoiceAgent as it now stands, so the screen redraws from the server's
 * word rather than from a guess about what the click did.
 */
import { ApiError, assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import { readBoolean, readRecord, readText } from "../decode.js";
import { buildJobOfWire, type BuildJobView } from "../build-view.js";
import type { VoiceAgentView } from "../gateway.js";
import { voiceAgentOfWire } from "../voice-agent-view.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

/** "reuse" builds the failed version again from its documents; "replace" waits for new ones. */
export type RebuildDocumentChoice = "reuse" | "replace";

export interface RebuildView {
  readonly buildJobId: string;
  /** `true` for "reuse" — the build is running; `false` for "replace" — it waits for documents. */
  readonly started: boolean;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** #106. Live → paused: callers are told the business cannot take their call right now. */
    pauseVoiceAgent(voiceAgentId: string): Promise<VoiceAgentView>;
    /** #106. Paused (by its owner) → live. */
    resumeVoiceAgent(voiceAgentId: string): Promise<VoiceAgentView>;
    /** #105. Leaves the list and every picker; its history is kept. */
    archiveVoiceAgent(voiceAgentId: string): Promise<VoiceAgentView>;
    /** Owner request 2026-10-28: a new name — no version, no build. */
    renameVoiceAgent(voiceAgentId: string, name: string): Promise<VoiceAgentView>;
    /** #32. Try a failed build again, with the same documents or new ones. */
    rebuildVoiceAgent(voiceAgentId: string, documents: RebuildDocumentChoice): Promise<RebuildView>;
    /** #28. One build as it stands now. */
    getBuildJob(buildJobId: string): Promise<BuildJobView>;
  }
}

/** Empty: all six operations are in the spec. */
export const voiceAgentLifecycleGap: readonly GatewayGap[] = [];

/** What the slice adds — the shape `api-gateway.ts` spreads. */
export interface VoiceAgentLifecycleGateway {
  pauseVoiceAgent(voiceAgentId: string): Promise<VoiceAgentView>;
  resumeVoiceAgent(voiceAgentId: string): Promise<VoiceAgentView>;
  archiveVoiceAgent(voiceAgentId: string): Promise<VoiceAgentView>;
  renameVoiceAgent(voiceAgentId: string, name: string): Promise<VoiceAgentView>;
  rebuildVoiceAgent(voiceAgentId: string, documents: RebuildDocumentChoice): Promise<RebuildView>;
  getBuildJob(buildJobId: string): Promise<BuildJobView>;
}

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

function voiceAgentOfLifecycle(body: unknown, operationId: string): VoiceAgentView {
  const data = readRecord(readRecord(body, operationId)["data"], `${operationId}.data`);
  return voiceAgentOfWire(data["voice_agent"], `${operationId}.data.voice_agent`);
}

export function voiceAgentLifecycleApiSlice(client: WeTalkApiClient, _context: SliceContext): VoiceAgentLifecycleGateway {
  return {
    async pauseVoiceAgent(voiceAgentId): Promise<VoiceAgentView> {
      const result = await client.pauseVoiceAgent({
        path: { voice_agent_id: voiceAgentId },
        header: { "idempotency-key": newIdempotencyKey() },
        body: {},
      });
      return voiceAgentOfLifecycle(assertOk(result), "pauseVoiceAgent");
    },

    async resumeVoiceAgent(voiceAgentId): Promise<VoiceAgentView> {
      const result = await client.resumeVoiceAgent({
        path: { voice_agent_id: voiceAgentId },
        header: { "idempotency-key": newIdempotencyKey() },
        body: {},
      });
      return voiceAgentOfLifecycle(assertOk(result), "resumeVoiceAgent");
    },

    async archiveVoiceAgent(voiceAgentId): Promise<VoiceAgentView> {
      const result = await client.archiveVoiceAgent({
        path: { voice_agent_id: voiceAgentId },
        header: { "idempotency-key": newIdempotencyKey() },
        body: {},
      });
      return voiceAgentOfLifecycle(assertOk(result), "archiveVoiceAgent");
    },

    async renameVoiceAgent(voiceAgentId, name): Promise<VoiceAgentView> {
      const result = await client.renameVoiceAgent({
        path: { voice_agent_id: voiceAgentId },
        header: { "idempotency-key": newIdempotencyKey() },
        body: { name },
      });
      return voiceAgentOfLifecycle(assertOk(result), "renameVoiceAgent");
    },

    async rebuildVoiceAgent(voiceAgentId, documents): Promise<RebuildView> {
      const result = await client.rebuildVoiceAgent({
        path: { voice_agent_id: voiceAgentId },
        header: { "idempotency-key": newIdempotencyKey() },
        body: { documents },
      });
      const data = readRecord(readRecord(assertOk(result), "rebuildVoiceAgent")["data"], "rebuildVoiceAgent.data");
      return {
        buildJobId: readText(data["build_job_id"], "rebuildVoiceAgent.build_job_id"),
        started: readBoolean(data["started"], "rebuildVoiceAgent.started"),
      };
    },

    async getBuildJob(buildJobId): Promise<BuildJobView> {
      const result = await client.getBuildJob({ path: { build_job_id: buildJobId } });
      return buildJobOfWire(readRecord(assertOk(result), "getBuildJob")["data"], "getBuildJob.data");
    },
  };
}

/**
 * The API's own sentence for a refused lifecycle action — it already says what to do ("Pause it
 * first", "ask WeTalk to move the number") — or the error's message.
 */
export function lifecycleRefusalOf(raised: unknown): string {
  if (raised instanceof ApiError || raised instanceof Error) return raised.message;
  return portalText("shell", "that_did_not_work_nothing_was_changed_4b96c983");
}
